import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { captureException } from '@sentry/react';
import { type FragmentType, getFragmentData, graphql } from 'client/gql';
import { useAuthClaims } from 'hooks/useAuthClaims';
import type React from 'react';
import { useCallback, useEffect } from 'react';
import { useMutation } from 'urql';
import { isIosOrAndroid } from 'utils/isIosOrAndroid';

const AddPushIdsInNotificationSettingsMutationDocument = graphql(`
  mutation AddPushIdsInNotificationSettings($add: NotificationSettingsUpdateInput!, $userUuid: ID!) {
    updateNotificationSettings(update: $add, where: { userUuid: $userUuid }) {
      notificationSettings {
        __typename
        userUuid
        pushIds
      }
    }
  }
`);
const CreatePushIdIfNoneIsDefinedInNotificationSettingsMutationDocument = graphql(`
  mutation CreatePushIdIfNoneIsDefinedInNotificationSettings($input: [NotificationSettingsCreateInput!]!) {
    createNotificationSettings(input: $input) {
      notificationSettings {
        __typename
        userUuid
        pushIds
      }
    }
  }
`);

const PushNotificationWrapperQueryFieldsDocument = graphql(`
  fragment PushNotificationWrapperFields on Query {
    notificationSettings(where: $notificationSettingsWhere) {
      __typename
      userUuid
      pushIds
    }
  }
`);

type PushNotificationWrapperProps = {
  children: React.ReactNode;
  query: FragmentType<typeof PushNotificationWrapperQueryFieldsDocument> | undefined;
  onChange: () => void;
};

export const PushNotificationWrapper = ({ children, query, onChange }: PushNotificationWrapperProps) => {
  const fragment = getFragmentData(PushNotificationWrapperQueryFieldsDocument, query);
  const notificationSettings = fragment?.notificationSettings;
  const { pimAuthClaims } = useAuthClaims();
  const userUuid = pimAuthClaims.getUser().uuid;
  const profileUuid = pimAuthClaims.getProfile().uuid;

  const notificationSettingsExistForUser = notificationSettings?.some((n) => n.userUuid === userUuid);

  const [, createPushId] = useMutation(CreatePushIdIfNoneIsDefinedInNotificationSettingsMutationDocument);
  const [, addPushId] = useMutation(AddPushIdsInNotificationSettingsMutationDocument);

  const saveTokenInDB = useCallback(
    async (token: string) => {
      if (notificationSettingsExistForUser) {
        await addPushId({
          userUuid,
          add: {
            pushIds_PUSH: [token],
            profile: [{ connect: [{ where: { node: { uuid: profileUuid } }, overwrite: true }] }],
          },
        });
      } else {
        await createPushId({
          input: {
            userUuid,
            pushIds: [token],
            profile: {
              connect: [{ where: { node: { uuid: profileUuid } }, overwrite: true }],
            },
          },
        });
      }
      onChange();
    },
    [createPushId, addPushId, profileUuid, notificationSettingsExistForUser, onChange, userUuid],
  );

  useEffect(() => {
    const currentPlatform = Capacitor.getPlatform();
    if (!isIosOrAndroid(currentPlatform)) return;

    const init = async () => {
      const { remove: removeRegistrationListener } = await listenToRegistration(saveTokenInDB);
      const { remove: removeErrorListener } = await listenToError();
      await registerNotifications();

      return () => {
        removeRegistrationListener();
        removeErrorListener();
      };
    };
    init();
  }, [saveTokenInDB]);

  return children;
};

const listenToRegistration = async (onTokenReturn: (token: string) => Promise<void>) =>
  PushNotifications.addListener('registration', async (token) => {
    await onTokenReturn(token.value);
  });

const listenToError = async () =>
  PushNotifications.addListener('registrationError', (err) => {
    captureException(err);
  });

const registerNotifications = async () => {
  let permStatus = await PushNotifications.requestPermissions();

  if (permStatus.receive === 'prompt') {
    permStatus = await PushNotifications.requestPermissions();
  }

  if (permStatus.receive === 'granted') {
    await PushNotifications.register();
  }
};
